<template>
  <div class="pa-4">
    <div class="d-flex justify-space-between align-center mb-4">
      <h2>Eigenschap Categorieën</h2>
      <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreateDialog">
        Categorie Toevoegen
      </v-btn>
    </div>

    <!-- Loading State -->
    <v-progress-linear v-if="store.loading" indeterminate></v-progress-linear>

    <!-- Error State -->
    <v-alert v-if="store.error" type="error" class="mb-4">
      {{ store.error }}
    </v-alert>

    <!-- Empty State -->
    <v-card
      v-if="!store.loading && store.categories.length === 0"
      class="pa-8 text-center"
    >
      <v-icon size="64" color="grey-lighten-1" class="mb-4"
        >mdi-tag-outline</v-icon
      >
      <h3 class="text-h6 mb-2">Geen Eigenschap Categorieën</h3>
      <p class="text-body-2 text-medium-emphasis mb-4">
        Begin door je eerste eigenschap categorie te maken.
      </p>
      <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreateDialog">
        Categorie Toevoegen
      </v-btn>
    </v-card>

    <!-- Trait Categories List -->
    <v-card v-if="!store.loading && store.categories.length > 0">
      <v-list>
        <v-list-item
          v-for="category in store.categories"
          :key="category.id"
          class="border-b"
        >
          <template #prepend>
            <v-icon color="primary">mdi-tag-multiple</v-icon>
          </template>

          <v-list-item-title>{{ category.name }}</v-list-item-title>

          <template #append>
            <div class="d-flex ga-2">
              <v-btn
                icon="mdi-pencil"
                size="small"
                variant="text"
                @click="openEditDialog(category)"
              ></v-btn>
              <v-btn
                icon="mdi-delete"
                size="small"
                variant="text"
                color="error"
                @click="confirmDelete(category)"
              ></v-btn>
            </div>
          </template>
        </v-list-item>
      </v-list>
    </v-card>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="dialog" max-width="500px" persistent>
      <v-card>
        <v-card-title>
          {{ isEditing ? "Categorie Bewerken" : "Categorie Toevoegen" }}
        </v-card-title>

        <v-card-text>
          <v-form ref="form" v-model="formValid">
            <v-text-field
              v-model="formData.name"
              label="Naam"
              hint="Naam van de eigenschap categorie (bijv. 'Gemoedstoestand')"
              persistent-hint
              :rules="nameRules"
              required
              autofocus
            ></v-text-field>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeDialog">Annuleren</v-btn>
          <v-btn
            color="primary"
            :loading="saving"
            :disabled="!formValid"
            @click="save"
          >
            {{ isEditing ? "Bijwerken" : "Maken" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title>Verwijdering Bevestigen</v-card-title>
        <v-card-text>
          Weet je zeker dat je "{{ categoryToDelete?.name }}" wilt verwijderen?
          Deze actie kan niet ongedaan worden gemaakt.

          <v-alert
            v-if="categoryToDelete"
            type="warning"
            variant="tonal"
            class="mt-4"
          >
            Let op: Als er eigenschappen gekoppeld zijn aan deze categorie, kan
            de categorie niet worden verwijderd.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="deleteDialog = false">Annuleren</v-btn>
          <v-btn color="error" :loading="deleting" @click="deleteCategory">
            Verwijderen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import { useTraitCategoriesStoreRefactor } from "@/stores/traitCategoriesStoreRefactor";

const store = useTraitCategoriesStoreRefactor();

// Data
const dialog = ref(false);
const deleteDialog = ref(false);
const formValid = ref(false);
const saving = ref(false);
const deleting = ref(false);
const isEditing = ref(false);
const categoryToDelete = ref(null);

const formData = reactive({
  id: null,
  name: "",
});

// Form validation rules
const nameRules = [
  (v) => !!v || "Naam is verplicht",
  (v) => (v && v.length <= 255) || "Naam moet minder dan 255 karakters zijn",
];

// Methods
const openCreateDialog = () => {
  isEditing.value = false;
  formData.id = null;
  formData.name = "";
  dialog.value = true;
};

const openEditDialog = (category) => {
  isEditing.value = true;
  formData.id = category.id;
  formData.name = category.name;
  dialog.value = true;
};

const closeDialog = () => {
  dialog.value = false;
  if (formData.form) {
    formData.form.reset();
  }
};

const save = async () => {
  if (!formValid.value) return;

  saving.value = true;

  try {
    const categoryData = {
      name: formData.name,
    };

    if (isEditing.value) {
      categoryData.id = formData.id;
      await store.update(categoryData);
    } else {
      await store.create(categoryData);
    }

    closeDialog();
  } catch (error) {
    console.error("Error saving trait category:", error);
  } finally {
    saving.value = false;
  }
};

const confirmDelete = (category) => {
  categoryToDelete.value = category;
  deleteDialog.value = true;
};

const deleteCategory = async () => {
  if (!categoryToDelete.value) return;

  deleting.value = true;

  try {
    await store.destroy(categoryToDelete.value.id);
    deleteDialog.value = false;
    categoryToDelete.value = null;
  } catch (error) {
    console.error("Error deleting trait category:", error);
  } finally {
    deleting.value = false;
  }
};

// Load data on mount
onMounted(async () => {
  await store.fetchAll();
});
</script>
